import { useSyncExternalStore } from "react";
import { loadQuoteStatuses } from "./client";
import type { QuoteStatus } from "./types";

// 報價單狀態的中文字樣,**後端是唯一來源**(設計規格 §1.4)。
//
// 下拉選單、狀態徽章、歷程與通知全部讀同一份 —— 前端再留一份寫死的對照表,
// 兩份遲早會漂開:這一輪就已經把 CANCELLED 從「已作廢」改成「已取消」,
// 那天漏斗顯示新字、徽章顯示舊字,而兩套測試都是綠的。
//
// 為什麼是一個模組層的小 store,而不是 context:
//
// * 徽章散在六個地方(列表、編輯器、頂列、封存區、範本頁、日曆卡),要它們
//   全部被同一個 Provider 包住,等於每加一個用到徽章的畫面就要記得包一次 ——
//   忘了包的那一個不會壞掉,只會安靜地顯示英文,而那正是這一輪要擋的事。
// * `loadQuoteStatuses()` 本來就有模組層快取,同一個分頁只會真的打一次 API;
//   這裡多的只是「載回來之後通知已經掛在畫面上的那幾顆徽章重畫」。
//
// **查不到就顯示識別字本身**(`DRAFT`),不在前端補一份中文 —— 與
// `QuoteEditor` 的狀態下拉同一種誠實的退路:那一瞬間畫面上會看到英文,
// 但它會自己好;前端自己補一份中文,則是又養了一份會漂開的對照表,
// 症狀跟這一瞬間長得一模一樣、卻永遠不會自己好。

type LabelMap = Partial<Record<QuoteStatus, string>>;

/** 載回來的那一份。`null` = 還沒載到(或這一趟失敗了)。 */
let labels: LabelMap | null = null;
/** 正在載。不擋的話,一次掛上十顆徽章就排十個 then。 */
let loading = false;
const listeners = new Set<() => void>();

function ensureLoaded(): void {
  if (labels !== null || loading) return;
  loading = true;
  loadQuoteStatuses()
    .then((rows) => {
      loading = false;
      // **空清單不進快取**,理由與 `loadQuoteStatuses()` 一字不差:一次網路抖動
      // 就讓這個分頁從此只顯示英文的話,使用者要重新整理整頁才救得回來,
      // 而他完全不知道自己為什麼要這麼做。
      if (rows.length === 0) return;
      const next: LabelMap = {};
      for (const row of rows) next[row.value] = row.label;
      labels = next;
      for (const notify of listeners) notify();
    })
    .catch(() => {
      loading = false;
    });
}

function subscribe(onChange: () => void): () => void {
  listeners.add(onChange);
  // 第一顆徽章掛上來時才去要 —— 沒有徽章的頁面(登入頁、設定頁)不必白打一支。
  ensureLoaded();
  return () => {
    listeners.delete(onChange);
  };
}

function snapshot(): LabelMap | null {
  return labels;
}

/**
 * 後端那一份字樣。還沒載到時是 `null`。
 *
 * 參考在載回來之前一直是同一個 `null`、之後一直是同一個物件,
 * `useSyncExternalStore` 才不會每次 render 都認為它變了。
 */
export function useQuoteStatusLabels(): LabelMap | null {
  return useSyncExternalStore(subscribe, snapshot, snapshot);
}

/** 這個狀態的中文字樣。查不到就回識別字本身 —— 徽章不可以是空白的。 */
export function useQuoteStatusLabel(status: QuoteStatus): string {
  const map = useQuoteStatusLabels();
  const label = map?.[status];
  return label != null && label !== "" ? label : status;
}
